import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ArrowRight, Share2 } from "lucide-react";
import api from "@/lib/api";
import { shareLink } from "@/lib/share";

function shuffle(a) {
  const x = [...a];
  for (let i = x.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [x[i], x[j]] = [x[j], x[i]];
  }
  return x;
}

export function QuotesCarousel() {
  const { data = [] } = useQuery({ queryKey: ["quotes"], queryFn: () => api.get("/quotes").then((r) => r.data) });
  const quotes = useMemo(() => shuffle(data), [data]);
  const [i, setI] = useState(0);

  useEffect(() => { setI(0); }, [quotes.length]);
  useEffect(() => {
    if (quotes.length < 2) return;
    const t = setInterval(() => setI((p) => (p + 1) % quotes.length), 6000);
    return () => clearInterval(t);
  }, [quotes.length]);

  if (!quotes.length) return null;
  const q = quotes[i % quotes.length];

  const shareQuote = () => shareLink({ title: `"${q.text}"`, note: `— ${q.author || "Unknown"}` });

  return (
    <section className="bg-foreground py-24 text-background md:py-32" data-testid="quotes-carousel">
      <div className="container-px mx-auto max-w-[1200px] text-center">
        <Quote className="mx-auto mb-8 h-8 w-8 text-background/40" />
        <div className="min-h-[240px] md:min-h-[260px]">
          <AnimatePresence mode="wait">
            <motion.blockquote key={q.id}
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="mx-auto max-w-4xl font-display text-3xl font-extrabold uppercase leading-tight md:text-5xl" data-testid="quote-text">
              “{q.text}”
              <footer className="mt-8 font-mono text-xs uppercase tracking-[0.3em] text-background/50">— {q.author || "Unknown"}</footer>
            </motion.blockquote>
          </AnimatePresence>
        </div>

        <div className="mt-10 flex items-center justify-center gap-6">
          <span className="font-mono text-xs uppercase tracking-widest text-background/50" data-testid="quote-counter">
            {String((i % quotes.length) + 1).padStart(2, "0")} / {String(quotes.length).padStart(2, "0")}
          </span>
          <button onClick={() => setI((p) => (p + 1) % quotes.length)} data-testid="quote-next"
            className="group flex items-center gap-2 border border-background/30 px-4 py-2 font-mono text-xs uppercase tracking-widest text-background transition-colors hover:bg-background hover:text-foreground">
            Next <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
          <button onClick={shareQuote} data-testid="quote-share"
            className="group flex items-center gap-2 bg-cyan px-4 py-2 font-mono text-xs uppercase tracking-widest text-white transition-colors hover:bg-cyan/90">
            <Share2 className="h-4 w-4" /> Share this quote
          </button>
        </div>
      </div>
    </section>
  );
}
